/* ============================================================
   Templary — Wishlist (localStorage, no app, no login).
   ------------------------------------------------------------
   - Stores product handles in localStorage.
   - Toggles hearts on product cards ([data-wishlist-toggle]).
   - Updates the header count ([data-wishlist-count]).
   - Renders the wishlist page ([data-wishlist-grid]) by fetching
     each product via the Ajax API (/products/<handle>.js).
   Per-device by design; a future account-synced version can layer
   on top without changing the UI.
   ============================================================ */
(() => {
  if (window.__twWishlistInit) return;
  window.__twWishlistInit = true;

  const KEY = 'templary:wishlist';

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full or blocked — ignore */
    }
  };

  const has = (handle) => read().indexOf(handle) !== -1;

  const toggle = (handle) => {
    const list = read();
    const i = list.indexOf(handle);
    if (i === -1) {
      list.push(handle);
    } else {
      list.splice(i, 1);
    }
    write(list);
    return list;
  };

  const refreshButtons = (root = document) => {
    const list = read();
    root.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const active = list.indexOf(btn.getAttribute('data-wishlist-handle')) !== -1;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const refreshCount = () => {
    const n = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = n > 0 ? String(n) : '';
      el.toggleAttribute('hidden', n === 0);
    });
  };

  const formatMoney = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  const renderPage = async () => {
    const grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    const empty = document.querySelector('[data-wishlist-empty]');
    const list = read();

    if (list.length === 0) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    const cards = await Promise.all(
      list.map((handle) =>
        fetch(`/products/${handle}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );

    grid.innerHTML = '';
    cards.forEach((p, idx) => {
      if (!p) return;
      const img = p.featured_image || (p.images && p.images[0]) || '';
      const el = document.createElement('div');
      el.className = 'tw-wish-card';
      el.innerHTML = `
        <button class="tw-wishlist-btn tw-wishlist-btn--on-card is-active" type="button" data-wishlist-toggle data-wishlist-handle="${p.handle}" aria-pressed="true" aria-label="Remove from wishlist">
          <svg class="tw-wishlist-btn__icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <a class="tw-wish-card__link" href="${p.url}">
          ${img ? `<img class="tw-wish-card__img" src="${img.replace('.jpg', '_600x.jpg').replace('.png', '_600x.png')}" alt="${(p.title || '').replace(/"/g, '&quot;')}" loading="lazy">` : '<span class="tw-wish-card__img tw-wish-card__img--empty"></span>'}
          <span class="tw-wish-card__title">${p.title || ''}</span>
          <span class="tw-wish-card__price">${formatMoney(p.price)}</span>
        </a>`;
      grid.appendChild(el);
    });
    refreshButtons(grid);
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.getAttribute('data-wishlist-handle');
    if (!handle) return;
    toggle(handle);
    refreshButtons();
    refreshCount();
    if (btn.closest('[data-wishlist-grid]')) renderPage();
  });

  const init = () => {
    refreshButtons();
    refreshCount();
    renderPage();
  };

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // Re-sync hearts on cards added later (infinite scroll, section renders).
  const observer = new MutationObserver(() => {
    clearTimeout(observer._t);
    observer._t = setTimeout(() => refreshButtons(), 120);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
